import React from 'react';
import { Input, Textarea } from '@fluentui/react-components';
import { useDispatch } from 'react-redux';
import { GetDataroomItemInfo, GetFolderDefaults, PostWSFolder } from '../../redux/dataroomSlice';
import NewProjectWizardCard from './NewProjectWizardCard';
import { catalogForWorkstream } from './vdrFolderCatalog';
import { suggestFolders } from './projectSetupApi';
import DocumentContextBanner from './DocumentContextBanner';
import SetupSuggestionLoader from './SetupSuggestionLoader';
import styles from './css/index.module.css';
import checkboxCheck from './assets/checkbox-check.svg';

const FOLDER_NAME_MAX = 100;

const nextKey = () => `folder-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

function textExamples(value) {
    const list = Array.isArray(value) ? value : value ? [value] : [];
    return list
        .map(item => {
            if (typeof item === 'string') return item.trim();
            if (item && typeof item === 'object') return String(item.name || item.title || item.document || '').trim();
            return '';
        })
        .filter(Boolean);
}

function examplesForEntry(entry, catalog = []) {
    const fromApi = textExamples(entry?.example_documents);
    if (fromApi.length) return fromApi;
    const fromAlt = textExamples(entry?.examples);
    if (fromAlt.length) return fromAlt;
    const name = (entry?.name || '').trim().toLowerCase();
    const match = (catalog || []).find(item => item.name.toLowerCase() === name);
    return textExamples(match?.examples);
}

function examplesForApi(value) {
    return (Array.isArray(value) ? value : [])
        .map(item => String(item || '').trim())
        .filter(Boolean);
}

function buildFoldersFromSuggestion(suggestionFolders = [], existing = [], workstreamName = '') {
    const catalog = catalogForWorkstream(workstreamName);
    const folders = (suggestionFolders || []).map(entry => ({
        key: nextKey(),
        name: entry.name,
        description: entry.description || '',
        recommended: Boolean(entry.recommended),
        selected: Boolean(entry.recommended),
        examples: examplesForEntry(entry, catalog),
        reason: entry.reason || '',
        order: entry.order,
    }));
    folders.sort((a, b) => (a.order || 0) - (b.order || 0));
    (existing || []).forEach(item => {
        if (!item?.name) return;
        const match = folders.find(folder => folder.name.toLowerCase() === item.name.toLowerCase());
        if (match) {
            match.existingId = item.id;
            match.selected = true;
            if (!match.description && item.description) match.description = item.description;
            return;
        }
        folders.push({
            key: `existing-${item.id}`,
            name: item.name,
            description: item.description || '',
            recommended: false,
            selected: true,
            existingId: item.id,
            examples: examplesForEntry(item, catalog),
            reason: '',
        });
    });
    return folders;
}

function buildFolders(workstreamName, apiDefaults = [], existing = []) {
    const catalog = catalogForWorkstream(workstreamName);
    const folders = [];

    catalog.forEach(entry => {
        folders.push({
            key: nextKey(),
            name: entry.name,
            description: entry.description || '',
            recommended: Boolean(entry.recommended),
            selected: Boolean(entry.recommended),
            examples: entry.examples || [],
        });
    });

    (apiDefaults || []).forEach((item, index) => {
        const name = (item.ws_folder || '').trim();
        if (!name) return;
        const match = folders.find(folder => folder.name.toLowerCase() === name.toLowerCase());
        if (match) {
            if (!match.description && item.ws_folder_desc) match.description = item.ws_folder_desc;
            return;
        }
        const recommend = catalog.length === 0 && index < Math.round((apiDefaults.length || 0) / 2);
        folders.push({
            key: nextKey(),
            name,
            description: item.ws_folder_desc || '',
            recommended: recommend,
            selected: recommend,
            examples: [],
        });
    });

    (existing || []).forEach(item => {
        if (!item?.name) return;
        const match = folders.find(folder => folder.name.toLowerCase() === item.name.toLowerCase());
        if (match) {
            match.existingId = item.id;
            match.selected = true;
            if (!match.description && item.description) match.description = item.description;
            return;
        }
        folders.push({
            key: `existing-${item.id}`,
            name: item.name,
            description: item.description || '',
            recommended: false,
            selected: true,
            existingId: item.id,
            examples: [],
        });
    });

    return folders;
}

const SetupVdrStructure = ({
    project,
    workstreams = [],
    onContinue,
    onExit,
    documentBanner,
    onDismissDocumentBanner,
    onRefreshRecommendations,
    refreshingRecommendations = false,
    onRefreshSettled,
    refreshToken = 0,
}) => {
    const dispatch = useDispatch();
    const [foldersByWs, setFoldersByWs] = React.useState({});
    const [activeWsId, setActiveWsId] = React.useState(workstreams[0]?.id || null);
    const [ready, setReady] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [editingKey, setEditingKey] = React.useState(null);
    const [draftName, setDraftName] = React.useState('');
    const [draftDescription, setDraftDescription] = React.useState('');
    const [expandedExamples, setExpandedExamples] = React.useState({});
    const [addingCustom, setAddingCustom] = React.useState(false);
    const [customName, setCustomName] = React.useState('');
    const [customDescription, setCustomDescription] = React.useState('');
    const [customExamples, setCustomExamples] = React.useState('');

    React.useEffect(() => {
        if (!workstreams.length) {
            setReady(true);
            onRefreshSettled?.();
            return undefined;
        }
        let cancelled = false;
        setReady(false);
        const load = async () => {
            let suggestionByWs = {};
            try {
                const suggestion = await suggestFolders(project?.id, workstreams);
                (suggestion?.workstreams || []).forEach(row => {
                    suggestionByWs[String(row.workstream_id)] = row.folders || [];
                });
            } catch {
                suggestionByWs = {};
            }
            const entries = await Promise.all(
                workstreams.map(async ws => {
                    const [defaultsRes, existingRes] = await Promise.all([
                        dispatch(GetFolderDefaults(ws.name)),
                        dispatch(GetDataroomItemInfo(ws.id)),
                    ]);
                    const apiDefaults = defaultsRes?.payload?.data || [];
                    const existing = existingRes?.payload?.data || [];
                    const suggested = suggestionByWs[String(ws.id)];
                    const folders = suggested?.length
                        ? buildFoldersFromSuggestion(suggested, existing, ws.name)
                        : buildFolders(ws.name, apiDefaults, existing);
                    return [ws.id, folders];
                }),
            );
            if (cancelled) return;
            setFoldersByWs(Object.fromEntries(entries));
            setActiveWsId(prev => prev || workstreams[0]?.id || null);
            setReady(true);
            onRefreshSettled?.();
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, project?.id, refreshToken, workstreams]);

    const activeWorkstream = workstreams.find(ws => ws.id === activeWsId) || workstreams[0];
    const folders = (activeWorkstream && foldersByWs[activeWorkstream.id]) || [];

    const updateFolders = (wsId, updater) => {
        setFoldersByWs(prev => ({
            ...prev,
            [wsId]: updater(prev[wsId] || []),
        }));
    };

    const startEdit = folder => {
        setAddingCustom(false);
        setExpandedExamples(prev => ({ ...prev, [folder.key]: false }));
        setEditingKey(folder.key);
        setDraftName(folder.name);
        setDraftDescription(folder.description || '');
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setDraftName('');
        setDraftDescription('');
    };

    const saveEdit = () => {
        if (!activeWorkstream) return;
        const name = draftName.trim();
        if (!name || name.length > FOLDER_NAME_MAX) return;
        const duplicate = folders.some(folder => folder.key !== editingKey && folder.name.toLowerCase() === name.toLowerCase());
        if (duplicate) return;
        updateFolders(activeWorkstream.id, list =>
            list.map(folder => (folder.key === editingKey ? { ...folder, name, description: draftDescription.trim() } : folder)),
        );
        cancelEdit();
    };

    const addCustom = () => {
        if (!activeWorkstream) return;
        const name = customName.trim();
        if (!name || name.length > FOLDER_NAME_MAX) return;
        if (folders.some(folder => folder.name.toLowerCase() === name.toLowerCase())) return;
        const examples = customExamples
            .split('\n')
            .map(line => line.replace(/^[·•\-\s]+/, '').trim())
            .filter(Boolean);
        updateFolders(activeWorkstream.id, list => [
            ...list,
            {
                key: nextKey(),
                name,
                description: customDescription.trim(),
                recommended: false,
                selected: true,
                examples,
            },
        ]);
        setCustomName('');
        setCustomDescription('');
        setCustomExamples('');
        setAddingCustom(false);
    };

    const selectAllRecommended = () => {
        if (!activeWorkstream) return;
        updateFolders(activeWorkstream.id, list => list.map(folder => (folder.recommended ? { ...folder, selected: true } : folder)));
    };

    const moveFolder = (index, direction) => {
        if (!activeWorkstream) return;
        const nextIndex = index + direction;
        if (nextIndex < 0 || nextIndex >= folders.length) return;
        updateFolders(activeWorkstream.id, list => {
            const next = [...list];
            const [item] = next.splice(index, 1);
            next.splice(nextIndex, 0, item);
            return next;
        });
    };

    const persist = async () => {
        for (const ws of workstreams) {
            const list = foldersByWs[ws.id] || [];
            const existingRes = await dispatch(GetDataroomItemInfo(ws.id));
            const existing = existingRes?.payload?.data || [];
            const existingNames = new Set(existing.map(folder => (folder.name || '').toLowerCase()));
            for (const folder of list) {
                if (!folder.selected) continue;
                if (existingNames.has(folder.name.toLowerCase())) continue;
                const result = await dispatch(
                    PostWSFolder({
                        name: folder.name,
                        description: folder.description || '--',
                        example_documents: examplesForApi(folder.examples),
                        requested: true,
                        workstream: ws.id,
                    }),
                );
                if (result?.type !== 'POST_IRL_ITEMS/fulfilled') {
                    const unique = result?.payload?.non_field_errors?.some(error =>
                        String(error).includes('The fields workstream, name must make a unique set'),
                    );
                    if (!unique) return false;
                }
            }
        }
        return true;
    };

    const handleCreate = async () => {
        if (saving) return;
        setSaving(true);
        const ok = await persist();
        setSaving(false);
        if (!ok) return;
        const folderCount = Object.values(foldersByWs).reduce(
            (sum, list) => sum + (list || []).filter(folder => folder.selected).length,
            0,
        );
        onContinue(folderCount);
    };

    const handleExit = async () => {
        if (saving) return;
        setSaving(true);
        const ok = await persist();
        setSaving(false);
        if (ok) onExit();
    };

    const continueDisabled = !ready || saving || Boolean(editingKey);

    return (
        <NewProjectWizardCard
            step={2}
            totalSteps={4}
            extraWide
            flushBody
            hideFooter
            title="Set up VDR structure"
            subtitle="Lens has recommended folders for each workstream. Review the structure before creating it."
            headerAside={<span className={styles.suggestedBadge}>Suggested by Lens</span>}
        >
            <div className={styles.vdrSplit}>
                <aside className={styles.vdrSidebar}>
                    <p className={styles.vdrSidebarLabel}>Workstreams</p>
                    {workstreams.map(ws => {
                        const list = foldersByWs[ws.id] || [];
                        const selected = list.filter(folder => folder.selected).length;
                        const active = ws.id === activeWorkstream?.id;
                        return (
                            <button
                                key={ws.id}
                                type="button"
                                className={`${styles.vdrSidebarItem} ${active ? styles.vdrSidebarItemActive : ''}`}
                                onClick={() => {
                                    setActiveWsId(ws.id);
                                    cancelEdit();
                                    setAddingCustom(false);
                                }}
                            >
                                <span className={styles.vdrSidebarName}>{ws.name}</span>
                                <span className={styles.vdrSidebarCount}>
                                    {selected}/{list.length}
                                </span>
                            </button>
                        );
                    })}
                </aside>

                <div className={styles.vdrMain}>
                    {documentBanner ? (
                        <DocumentContextBanner
                            variant={documentBanner}
                            onDismiss={onDismissDocumentBanner}
                            onRefresh={onRefreshRecommendations}
                            refreshing={refreshingRecommendations}
                            onNotNow={onDismissDocumentBanner}
                        />
                    ) : null}
                    <div className={styles.vdrMainHeader}>
                        <div>
                            <h2 className={styles.vdrMainTitle}>{activeWorkstream?.name || 'Workstream'}</h2>
                            <p className={styles.vdrMainCount}>
                                {folders.filter(folder => folder.selected).length} of {folders.length} folders selected
                            </p>
                        </div>
                        <button type="button" className={styles.vdrSelectAll} onClick={selectAllRecommended}>
                            Select all recommended
                        </button>
                    </div>

                    <div className={styles.vdrList}>
                        {!ready ? <SetupSuggestionLoader label="Loading recommended folders…" /> : null}
                        {ready && folders.length === 0 && !addingCustom ? (
                            <p className={styles.vdrEmpty}>No recommended folders yet. Add a custom folder to get started.</p>
                        ) : null}
                        {folders.map((folder, index) => (
                            <FolderRow
                                key={folder.key}
                                folder={folder}
                                editing={editingKey === folder.key}
                                expanded={Boolean(expandedExamples[folder.key])}
                                isFirst={index === 0}
                                isLast={index === folders.length - 1}
                                draftName={draftName}
                                draftDescription={draftDescription}
                                onDraftName={setDraftName}
                                onDraftDescription={setDraftDescription}
                                onToggle={() =>
                                    updateFolders(activeWorkstream.id, list =>
                                        list.map(item => (item.key === folder.key ? { ...item, selected: !item.selected } : item)),
                                    )
                                }
                                onEdit={() => startEdit(folder)}
                                onSave={saveEdit}
                                onCancel={cancelEdit}
                                onToggleExamples={() =>
                                    setExpandedExamples(prev => ({ ...prev, [folder.key]: !prev[folder.key] }))
                                }
                                onMoveUp={() => moveFolder(index, -1)}
                                onMoveDown={() => moveFolder(index, 1)}
                            />
                        ))}

                        {addingCustom ? (
                            <div className={styles.vdrCustomForm}>
                                <Input
                                    className={styles.textInput}
                                    value={customName}
                                    onChange={e => setCustomName(e.target.value)}
                                    placeholder="Folder name (required)"
                                    maxLength={FOLDER_NAME_MAX}
                                    autoFocus
                                />
                                <Textarea
                                    className={`${styles.textareaInput} ${styles.wsDescriptionInput}`}
                                    value={customDescription}
                                    onChange={e => setCustomDescription(e.target.value)}
                                    placeholder="Description"
                                    rows={2}
                                    resize="none"
                                />
                                <Textarea
                                    className={`${styles.textareaInput} ${styles.wsDescriptionInput}`}
                                    value={customExamples}
                                    onChange={e => setCustomExamples(e.target.value)}
                                    placeholder="Example documents, one per line"
                                    rows={2}
                                    resize="none"
                                />
                                <div className={styles.wsEditActions}>
                                    <button type="button" className={styles.primaryButton} disabled={!customName.trim()} onClick={addCustom}>
                                        Add folder
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.ghostButton}
                                        onClick={() => {
                                            setAddingCustom(false);
                                            setCustomName('');
                                            setCustomDescription('');
                                            setCustomExamples('');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className={styles.addScopeLink}
                                onClick={() => {
                                    cancelEdit();
                                    setAddingCustom(true);
                                }}
                            >
                                + Add custom folder
                            </button>
                        )}
                    </div>

                    <div className={styles.vdrFooter}>
                        <button
                            type="button"
                            className={`${styles.primaryButton} ${saving ? styles.primaryButtonLoading : ''}`}
                            disabled={continueDisabled}
                            aria-busy={saving || undefined}
                            onClick={handleCreate}>
                            Create VDR structure
                            {saving ? <span className={styles.buttonSpinner} aria-hidden="true" /> : null}
                        </button>
                        <button type="button" className={styles.ghostButton} disabled={saving} onClick={handleExit}>
                            Save & exit
                        </button>
                    </div>
                </div>
            </div>
        </NewProjectWizardCard>
    );
};

const FolderRow = ({
    folder,
    editing,
    expanded,
    isFirst,
    isLast,
    draftName,
    draftDescription,
    onDraftName,
    onDraftDescription,
    onToggle,
    onEdit,
    onSave,
    onCancel,
    onToggleExamples,
    onMoveUp,
    onMoveDown,
}) => {
    if (editing) {
        return (
            <div className={`${styles.vdrRow} ${styles.vdrRowSelected}`}>
                <div className={styles.wsEditFields}>
                    <Input className={styles.textInput} value={draftName} onChange={e => onDraftName(e.target.value)} maxLength={FOLDER_NAME_MAX} />
                    <Textarea
                        className={`${styles.textareaInput} ${styles.wsDescriptionInput}`}
                        value={draftDescription}
                        onChange={e => onDraftDescription(e.target.value)}
                        rows={2}
                        resize="none"
                    />
                    <div className={styles.wsEditActions}>
                        <button type="button" className={styles.primaryButton} disabled={!draftName.trim()} onClick={onSave}>
                            Save
                        </button>
                        <button type="button" className={styles.ghostButton} onClick={onCancel}>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const selected = folder.selected;

    return (
        <div className={`${styles.vdrRow} ${selected ? styles.vdrRowSelected : styles.vdrRowIdle}`}>
            <button
                type="button"
                role="checkbox"
                aria-checked={selected}
                className={`${styles.wsCheckbox} ${selected ? styles.wsCheckboxChecked : ''}`}
                onClick={onToggle}
            >
                {selected ? <img src={checkboxCheck} alt="" width={11} height={11} className={styles.wsCheckboxCheck} /> : null}
            </button>
            <div className={styles.vdrRowBody}>
                <div className={styles.vdrRowTop}>
                    <div className={styles.vdrRowCopy}>
                        <div className={styles.vdrNameRow}>
                            <span className={styles.vdrName}>{folder.name}</span>
                            {folder.recommended ? <span className={styles.vdrRecommended}>· Recommended</span> : null}
                        </div>
                        {folder.description ? <p className={styles.vdrDescription}>{folder.description}</p> : null}
                        {folder.reason ? <p className={styles.irlWhy}>{folder.reason}</p> : null}
                    </div>
                    <div className={styles.vdrRowActions}>
                        <button type="button" className={styles.vdrTextLink} onClick={onEdit}>
                            Edit
                        </button>
                        <button type="button" className={styles.vdrTextLink} onClick={onToggleExamples}>
                            {expanded ? 'Hide' : 'Example documents'}
                        </button>
                    </div>
                </div>
                {expanded ? (
                    <div className={styles.vdrExamples}>
                        <p className={styles.vdrExamplesTitle}>Example documents</p>
                        {folder.examples?.length ? (
                            <ul className={styles.vdrExamplesList}>
                                {folder.examples.map(example => (
                                    <li key={example}>· {example}</li>
                                ))}
                            </ul>
                        ) : (
                            <p className={styles.vdrDescription}>No example documents yet.</p>
                        )}
                    </div>
                ) : null}
            </div>
            <div className={styles.vdrReorder}>
                <button type="button" className={styles.vdrReorderBtn} disabled={isFirst} onClick={onMoveUp} aria-label="Move up">
                    ↑
                </button>
                <button type="button" className={styles.vdrReorderBtn} disabled={isLast} onClick={onMoveDown} aria-label="Move down">
                    ↓
                </button>
            </div>
        </div>
    );
};

export default SetupVdrStructure;
